import React, { useState, useCallback, useEffect } from 'react';
import { useTheme } from '@emotion/react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    Grid,
    Box,
    Typography,
    TextField,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MainCard from 'ui-component/cards/MainCard';
import CreatableSelect from 'react-select/creatable';
import { Controller, useForm } from 'react-hook-form';
import Editor from 'ui-component/QuillEditor/Editor';
import { useDropzone } from 'react-dropzone';
import { post, put, get } from '../../axios/apiService';
import uploadFilesService from '../../axios/uploadFilesService';
import toast from 'react-hot-toast';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import { FileCategory } from '../../enums/Enumerations';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';

const components = {
    DropdownIndicator: null,
};

const createOption = (label) => ({
    label,
    value: label,
});

function AddEditBlogForm({ title = '' }) {
    const theme = useTheme();
    const id = useParams();
    const navigate = useNavigate();
    const [isLoading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const { hasWritePermission } = usePermissions();
    const canEdit = hasWritePermission(MODULE_KEYS.Blogs)

    const { control, handleSubmit, setValue, getValues, reset, formState: { errors } } = useForm({
        defaultValues: {
            title: '',
            content: '',
            metaKeywords: [],
            documents: [],
        },
        mode: 'onChange',
    });

    // Load blog details for edit
    useEffect(() => {
        if (id.id) {
            const getById = async () => {
                setLoading(true);
                try {
                    let response = await get(`Blog/${id.id}`);

                    if (response && response.result) {
                        const resetdata = reset({
                            title: response.result?.title || '',
                            content: response.result?.description || '',

                            metaKeywords: typeof response.result.metaKeyWords === 'string'
                                ? response.result.metaKeyWords
                                    .split(',')
                                    .map(keyword => ({
                                        label: keyword.trim(),
                                        value: keyword.trim(),
                                    }))
                                : [],

                            documents: Array.isArray(response.result.attachments)
                                ? response.result.attachments.map(attachment => {
                                    const url = attachment?.attachmentUrl || '';
                                    const ext = url.split('.').pop()?.toLowerCase();

                                    let type = '';
                                    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
                                        type = `image/*`;
                                    } else if (['mp4', 'mov', 'avi'].includes(ext)) {
                                        type = `video/*`;
                                    } else {
                                        type = 'file';
                                    }

                                    return {
                                        name: url ? url.split('/').pop() : 'unknown',
                                        type: 'image/jpeg',
                                        file: null,
                                        preview: url || null,
                                    };
                                })
                                : [],
                        });
                    }
                } catch (err) {
                    toast.error('Error loading blog');
                } finally {
                    setLoading(false);
                }
            };
            getById();
        }
    }, [id]);

    const validator = () => {
        const data = getValues();
        const errors = {};
        if (!data.title.trim()) errors.title = 'Title is required';
        if (!data.content.trim() || data.content === '<p><br></p>') errors.content = 'Content is required';
        if (!data.metaKeywords || data.metaKeywords.length === 0) errors.metaKeywords = 'At least one meta keyword is required';

        if (!data.documents || data.documents.length === 0) {
            errors.documents = 'At least one document is required';
        } else {
            const oversizedFiles = data.documents.filter(
                doc => doc.file instanceof File && doc.file.size > 10 * 1024 * 1024
            );

            if (oversizedFiles.length > 0) {
                errors.documents = 'Some files exceed the 10MB limit';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const onSubmit = async (data, e) => {
        setLoading(true);
        if (!validator()) {
            return;
        }

        try {
            await saveBlog(data, e);
            setLoading(false);
        } catch (error) {
            toast.error(error?.response?.data || "Error in onsubmit call");
        } finally {
            setLoading(false);
        }
    };

    const saveBlog = async (data, e) => {
        setLoading(true);
        try {
            const clickedButton = e?.nativeEvent?.submitter?.value;
            const isPublished = clickedButton === "publish";

            const existingAttachments = data.documents
                .filter(doc => !doc.file)
                .map(doc => ({
                    AttachmentUrl: doc.preview || doc.name,
                }));

            const newFiles = data.documents.filter(doc => doc.file instanceof File);

            let uploadedAttachments = [];
            if (newFiles.length > 0) {
                const formData = new FormData();
                newFiles.forEach(doc => formData.append("files", doc.file));

                const fileUploadResponse = await uploadFilesService(formData, FileCategory.BLOG);

                uploadedAttachments = fileUploadResponse.map(f => ({
                    AttachmentUrl: f.fileUrl,
                }));
            }

            const finalAttachments = [...existingAttachments, ...uploadedAttachments];

            const blogPayload = {
                title: data.title,
                description: data.content,
                metaKeyWords: data.metaKeywords.map(k => k.label).join(","),
                attachments: finalAttachments,
                isPublished: isPublished
            };

            const blogApiUrl = id.id ? `Blog/${id.id}` : `Blog`;
            const blogResponse = id.id
                ? await put(blogApiUrl, blogPayload)
                : await post(blogApiUrl, blogPayload);

            toast.success(id.id ? "Blog updated successfully" : "Blog added successfully");
            navigate("/blog");
        } catch (error) {
            toast.error(error?.response?.data || "Error saving blog");
        } finally {
            setLoading(false);
        }
    };


    const [inputValue, setInputValue] = useState('');
    const [value, setCreatableSelectValue] = useState([]);

    useEffect(() => {
        setCreatableSelectValue(getValues('metaKeywords') || []);
    }, [getValues('metaKeywords')]);

    const handleKeyDown = (event) => {
        if (!inputValue.trim()) return;
        switch (event.key) {
            case 'Enter':
            case 'Tab':
                setCreatableSelectValue((prev) => [...prev, createOption(inputValue.trim())]);
                setInputValue('');
                setValue('metaKeywords', [...value, createOption(inputValue.trim())], { shouldValidate: true });
                event.preventDefault();
                break;
            default:
                break;
        }
    };

    const onDrop = useCallback((acceptedFiles) => {
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        const validFiles = acceptedFiles.filter(file => file.size <= maxSize);
        const rejectedFiles = acceptedFiles.filter(file => file.size > maxSize);

        const newFormErrors = { ...formErrors };
        if (rejectedFiles.length > 0) {
            newFormErrors.documents = 'Some files exceed the 10MB limit';
        } else if (validFiles.length === 0 && (getValues('documents') || []).length === 0) {
            newFormErrors.documents = 'At least one document is required';
        } else {
            delete newFormErrors.documents; // Clear document-related errors if all files are valid
        }
        setFormErrors(newFormErrors);

        if (validFiles.length > 0) {
            const filesWithPreview = validFiles.map(file => ({
                file,
                preview: file.type.startsWith('image/') || file.type.startsWith('video/') ? URL.createObjectURL(file) : null,
                name: file.name,
                type: file.type,
            }));
            const currentDocuments = getValues('documents') || [];
            const updatedDocuments = [...currentDocuments, ...filesWithPreview];
            setValue('documents', updatedDocuments, { shouldValidate: true });
        }
    }, [getValues, setValue, formErrors]);

    const removeFile = (fileToRemove, fieldValue, fieldOnChange) => {

        const updatedFiles = fieldValue.filter(
            f => !(f.name === fileToRemove.name && f.preview === fileToRemove.preview)
        );
        if (fileToRemove.file && fileToRemove.preview?.startsWith("blob:")) {
            URL.revokeObjectURL(fileToRemove.preview);
        }
        fieldOnChange(updatedFiles);

        const newFormErrors = { ...formErrors };
        if (updatedFiles.length === 0) {
            newFormErrors.documents = 'At least one document is required';
        } else {
            const oversizedFiles = updatedFiles.filter(
                doc => doc.file instanceof File && doc.file.size > 10 * 1024 * 1024
            );
            if (oversizedFiles.length > 0) {
                newFormErrors.documents = 'Some files exceed the 10MB limit';
            } else {
                delete newFormErrors.documents;
            }
        }
        setFormErrors(newFormErrors);
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
            'video/*': ['.mp4', '.mov', '.avi'],
        },
        multiple: true,
        onDrop,
    });

    useEffect(() => {
        return () => {
            const documents = getValues('documents') || [];
            documents.forEach(fileObj => {
                if (fileObj.preview) {
                    URL.revokeObjectURL(fileObj.preview);
                }
            });
        };
    }, [getValues]);

    return (
        <div>
            <MainCard title={title} backButton={{ onClick: () => navigate('/blog') }}>
                <form onSubmit={handleSubmit((data, e) => onSubmit(data, e))}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <fieldset
                                style={{
                                    border: '2px solid #364152',
                                    borderRadius: '8px',
                                    padding: '20px',
                                    marginBottom: '20px',
                                }}>
                                <legend
                                    style={{
                                        color: '#364152',
                                        fontSize: '1.2em',
                                        padding: '0 10px',
                                        fontWeight: 'bold',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '5px',
                                    }}
                                >
                                    Blog Details
                                </legend>
                                <Grid container spacing={2}>
                                    {/* Blog Title */}
                                    <Grid item xs={12} sm={12} lg={12}>
                                        <Controller
                                            name="title"
                                            control={control}
                                            rules={{ required: 'Title is required' }}
                                            render={({ field, fieldState }) =>
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    value={field.value}
                                                    label="Blog Title *"
                                                    error={!!fieldState.error || !!formErrors.title}
                                                    helperText={fieldState.error?.message || formErrors.title}
                                                    disabled={!canEdit}
                                                />
                                            }
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2} sx={{ marginTop: '10px' }}>
                                    {/* Blog Description */}
                                    <Grid item xs={12}>
                                        <Controller
                                            name="content"
                                            control={control}
                                            rules={{
                                                required: 'Content is required',
                                                validate: value => value.trim() !== '' && value !== '<p><br></p>' || 'Content cannot be empty',
                                            }}
                                            render={({ field, fieldState }) =>
                                                <Box>
                                                    <Editor
                                                        value={field.value}
                                                        onChange={(value) => field.onChange(value)}
                                                        readOnly={!canEdit}
                                                        isBlog={true}
                                                    />
                                                    {(fieldState.error || formErrors.content) && (
                                                        <Typography color="error" variant="caption">
                                                            {fieldState.error?.message || formErrors.content}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2} sx={{ marginTop: '10px' }}>
                                    {/* Meta Keywords */}
                                    <Grid item xs={12} sm={12} lg={12}>
                                        <Controller
                                            name="metaKeywords"
                                            control={control}
                                            rules={{
                                                required: 'At least one meta keyword is required',
                                            }}
                                            render={({ field, fieldState }) => (
                                                <Box>
                                                    <CreatableSelect
                                                        components={components}
                                                        inputValue={inputValue}
                                                        isClearable
                                                        isMulti
                                                        menuIsOpen={false}
                                                        onChange={(newValue) => {
                                                            field.onChange(newValue);
                                                            setCreatableSelectValue(newValue);
                                                        }}
                                                        onInputChange={(newValue) => {
                                                            setInputValue(newValue);
                                                        }}
                                                        onKeyDown={handleKeyDown}
                                                        placeholder="Type something and press enter for new meta keyword tag..."
                                                        value={field.value}
                                                        isDisabled={!canEdit}
                                                    />
                                                    {(fieldState.error || formErrors.metaKeywords) && (
                                                        <Typography color="error" variant="caption">
                                                            {fieldState.error?.message || formErrors.metaKeywords}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2} sx={{ marginTop: '10px' }}>
                                    {/* Upload Documents */}
                                    <Grid item xs={12} sm={12} lg={12}>
                                        <Controller
                                            name="documents"
                                            control={control}
                                            rules={{
                                                required: false,
                                                validate: value => {
                                                    if (!value || value.length === 0) return 'At least one document is required';
                                                    const oversizedFiles = value.filter(
                                                        doc => doc.file instanceof File && doc.file.size > 10 * 1024 * 1024
                                                    );
                                                    return oversizedFiles.length === 0 || 'Some files exceed the 10MB limit';
                                                }


                                            }}
                                            render={({ field, fieldState }) => (
                                                <Box>
                                                    {
                                                        canEdit && (
                                                            <Box
                                                                sx={{
                                                                    border: '2px dashed #364152',
                                                                    borderRadius: '8px',
                                                                    padding: '20px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: '#f9f9f9',
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        backgroundColor: '#f0f0f0',
                                                                    },
                                                                }}
                                                                {...getRootProps()}
                                                            >
                                                                <input {...getInputProps()} />
                                                                <Typography variant="body1">
                                                                    Drag & drop images or videos here, or click to select files
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    (Accepted formats: PNG, JPG, JPEG, GIF, MP4, MOV, AVI)
                                                                    <br />
                                                                    (Max size per file: 10MB)
                                                                </Typography>
                                                            </Box>
                                                        )
                                                    }
                                                    {(fieldState.error || formErrors.documents) && (
                                                        <Typography color="error" variant="caption">
                                                            {fieldState.error?.message || formErrors.documents}
                                                        </Typography>
                                                    )}
                                                    {/* File Previews */}
                                                    {field.value && field.value.length > 0 && (
                                                        <Box sx={{ marginTop: '20px' }}>
                                                            <Typography variant="subtitle1">Uploaded Files:</Typography>
                                                            <Grid container spacing={2}>
                                                                {field.value.map((fileObj, index) => (
                                                                    <Grid item xs={6} sm={4} md={3} key={index} style={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
                                                                        <Box
                                                                            sx={{
                                                                                border: '1px solid #ddd',
                                                                                borderRadius: '8px',
                                                                                padding: '10px',
                                                                                textAlign: 'center',
                                                                                position: 'relative',
                                                                                width: '150px',
                                                                                height: '150px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                overflow: 'hidden',
                                                                            }}
                                                                        >
                                                                            <IconButton
                                                                                onClick={() => {
                                                                                    removeFile(fileObj, field.value, field.onChange);
                                                                                }}
                                                                                sx={{
                                                                                    position: 'absolute',
                                                                                    top: '1px',
                                                                                    right: '1px',
                                                                                    color: 'error.main',
                                                                                    padding: '8px',
                                                                                }}
                                                                            >
                                                                                <CloseIcon fontSize="small" />
                                                                            </IconButton>

                                                                            {fileObj.type.startsWith('image') && fileObj.preview ? (
                                                                                <img
                                                                                    src={fileObj.preview}
                                                                                    alt={fileObj.name}
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        objectFit: 'cover',
                                                                                        borderRadius: '4px',
                                                                                    }}
                                                                                />
                                                                            ) : fileObj.type.startsWith('video') && fileObj.preview ? (
                                                                                <video
                                                                                    src={fileObj.preview}
                                                                                    controls
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        objectFit: 'cover',
                                                                                        borderRadius: '4px',
                                                                                    }}
                                                                                >
                                                                                    <track kind="captions" src="" label="No captions available" />
                                                                                </video>
                                                                            ) : (
                                                                                <Typography variant="body2">{fileObj.name}</Typography>
                                                                            )}
                                                                        </Box>
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </fieldset>
                        </Grid>
                        {
                            canEdit && (
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        <Grid item>
                                            <Button
                                                type="submit"
                                                value="publish"
                                                variant="contained"
                                                sx={{ marginTop: '20px', padding: '5px 30px' }}
                                                disabled={!canEdit}>
                                                Publish
                                            </Button>
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                type="submit"
                                                value="draft"
                                                variant="contained"
                                                sx={{ marginTop: '20px', padding: '5px 30px' }}
                                                disabled={!canEdit}
                                            >
                                                Draft
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )
                        }

                    </Grid>
                </form>
            </MainCard>

            <LoadingBackdrop open={isLoading} />

        </div>
    );
}

export default AddEditBlogForm;